import { gql } from 'graphql-tag';
import React, { createContext, FunctionComponent, ReactNode, useContext } from 'react';

import { AccountFeature, AccountFeatures } from '../chromatic-lib/constants/AccountFeature';
import { FeatureFlagsAccountFeaturesFragment } from './__generated__/useFeatureFlag.types';

export const fragmentFeatureFlagsAccountFeatures = gql`
  fragment FeatureFlagsAccountFeatures on AccountFeatures {
    id
    betaCaptureStack
    customDomains
    publicApi
    chInteractionTests
    unlinkedUIReview
    linkOnPrem
    useNextExtract
    useFigmaEmbedKit
    samlAndGit
    preferMergedBaselines
    modesFeatureFlag
    commitStatusFailAsPendingFeatureFlag
    uploadBuildDeduplication
    webhooksFeatureFlag
    ghCacheFeatureFlag
    diffShiftDetection
    accessibilityTests
    gitHubAppForRequests
    accessibilityDashboard
    accessibilityBilling
    accessibilityPlus
    newBillingPlans
    useBuildLifecycleWorkflow
    updatedLastBuildQuery
    uiReviewImprovements
  }
`;

interface FeatureFlagsProviderState {
  features: AccountFeatures;
}

interface FeatureFlagsProviderProps {
  children: ReactNode;
  features: AccountFeatures;
}

export const defaultFeatures: FeatureFlagsAccountFeaturesFragment = {
  betaCaptureStack: false,
  customDomains: false,
  publicApi: false,
  chInteractionTests: false,
  unlinkedUIReview: false,
  linkOnPrem: false,
  useNextExtract: false,
  useFigmaEmbedKit: false,
  samlAndGit: false,
  preferMergedBaselines: false,
  modesFeatureFlag: false,
  commitStatusFailAsPendingFeatureFlag: false,
  uploadBuildDeduplication: false,
  webhooksFeatureFlag: false,
  ghCacheFeatureFlag: false,
  diffShiftDetection: false,
  accessibilityTests: false,
  gitHubAppForRequests: false,
  accessibilityDashboard: false,
  accessibilityBilling: false,
  accessibilityPlus: false,
  newBillingPlans: false,
  useBuildLifecycleWorkflow: false,
  updatedLastBuildQuery: false,
  uiReviewImprovements: false,
};

const FeatureFlagsContext = createContext<FeatureFlagsProviderState>({
  features: defaultFeatures,
});

export const FeatureFlagsProvider: FunctionComponent<
  React.PropsWithChildren<FeatureFlagsProviderProps>
> = ({ children, features }) => (
  <FeatureFlagsContext.Provider value={{ features }}>{children}</FeatureFlagsContext.Provider>
);

const useFeatureFlag = (featureFlag: AccountFeature) => {
  const { features } = useContext(FeatureFlagsContext);
  return Boolean(features?.[featureFlag]);
};

export default useFeatureFlag;
